import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Section } from './DetailPrimitives';
import { AgentFamilyList, UsageFamily } from './UsedByTemplatesSection';
import { hrefForImpactRow, reachedViaLabel } from './ontologyFormat';
import { normalizeImpact, ontologyErrorMessage, sourceDocumentsApi } from '../../api/ontologyApi';
import { dsVar, ink } from '../../theme/tokens';

/**
 * THE BLAST RADIUS OF ONE DOCUMENT — what a change to it would reach.
 *
 * The counterpart of OntologyReachRail, which answers the same question for a term, a
 * policy or a threshold. This is the fourth entry point: "we are rewriting this policy" or
 * "the regulator reissued this guidance" — what moves?
 *
 * Read NEAREST FIRST, and every row says HOW it was reached, because the tiers are not
 * equally strong evidence. "Cites this document" is a link somebody authored. "Under an
 * affected policy" is a transitive consequence that on real data drags in most of the
 * threshold set — 45 rows against 2 for the FFIEC manual, because one broad policy governs
 * 48 concepts. Presenting those as one flat list makes the honest answer ("this touches
 * most of the system") true and useless, so the wide tier is collapsed, labelled with its
 * count, and fetched only when opened.
 */

interface ImpactRow {
    kind: string;
    code: string;
    label: string;
    scope_level?: string | null;
    status?: string | null;
    in_force?: boolean;
    reached_via?: string | null;
    /** The document names a code that resolves to nothing the caller can see. */
    unresolved?: boolean;
    document_identifier?: string | null;
}

interface ImpactData {
    totals: Record<string, number>;
    tiers: {
        citedBy: ImpactRow[];
        governs: ImpactRow[];
        governingPolicies: ImpactRow[];
        thresholds: ImpactRow[];
        agents: UsageFamily[];
        wideParameters: ImpactRow[];
    };
    wideIncluded: boolean;
    implements: ImpactRow[];
    implementedBy: ImpactRow[];
}

/** One reached record: what it is, and how the document reaches it. */
const ImpactRowItem: React.FC<{ row: ImpactRow; showVia?: boolean }> = ({ row, showVia = true }) => {
    const href = row.unresolved ? null : hrefForImpactRow(row.kind, row.code);

    return (
        <Box
            sx={{
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                gap: 1.5, px: 1.5, py: 1,
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                {href ? (
                    <Box
                        component="a"
                        href={href}
                        sx={{
                            fontSize: '0.875rem', color: dsVar('accent-fg'), textDecoration: 'none',
                            wordBreak: 'break-word',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        {row.label}
                    </Box>
                ) : (
                    <Typography component="span" sx={{ fontSize: '0.875rem', color: dsVar('fg-2') }}>
                        {row.label}
                    </Typography>
                )}

                {/* A parameter's label IS its code, so printing both stutters. Concepts
                    and policies have a real label and the code underneath it earns its
                    place. */}
                {row.label !== row.code && (
                    <Typography
                        component="div"
                        sx={{
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: '0.6875rem', color: dsVar('fg-muted'), mt: 0.25, wordBreak: 'break-all',
                        }}
                    >
                        {row.code}
                    </Typography>
                )}

                {/* A code the document names that resolves to nothing is the document's
                    most serious problem, not a row to render blank. */}
                {row.unresolved && (
                    <Typography sx={{ fontSize: '0.6875rem', color: ink('#F59E0B'), mt: 0.25 }}>
                        No longer defined
                    </Typography>
                )}

                {/* Approved-and-within-dates is not the same as approved. A draft or a
                    future-dated version is in the radius but is not what is running. */}
                {!row.unresolved && row.in_force === false && (
                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mt: 0.25 }}>
                        {row.status === 'draft' ? 'draft · not yet in force' : 'approved · not in force'}
                    </Typography>
                )}
            </Box>

            {showVia && row.reached_via && (
                <Typography
                    component="span"
                    sx={{
                        flexShrink: 0, fontSize: '0.6875rem', color: dsVar('fg-muted'),
                        textAlign: 'right', mt: '2px',
                    }}
                >
                    {reachedViaLabel(row.reached_via)}
                </Typography>
            )}
        </Box>
    );
};

const RowList: React.FC<{ rows: ImpactRow[]; showVia?: boolean }> = ({ rows, showVia }) => (
    <Box sx={{ border: `1px solid ${dsVar('line')}`, borderRadius: '6px', overflow: 'hidden' }}>
        {rows.map((row, index) => (
            <Box
                key={`${row.kind}:${row.code}`}
                sx={{ borderTop: index === 0 ? 'none' : `1px solid ${dsVar('line')}` }}
            >
                <ImpactRowItem row={row} showVia={showVia} />
            </Box>
        ))}
    </Box>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-2') }}>{children}</Typography>
);

const DocumentImpactPanel: React.FC<{ code: string }> = ({ code }) => {
    const [data, setData] = useState<ImpactData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // The wide tier is a separate request, made only when the disclosure is opened.
    const [wideOpen, setWideOpen] = useState(false);
    const [wideLoading, setWideLoading] = useState(false);
    // Held so switching document while the wide tier is in flight cancels it, rather than
    // letting a slow response overwrite the new document's report with the old one's.
    const wideRequest = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!code) return undefined;

        const controller = new AbortController();
        let active = true;

        (async () => {
            setLoading(true);
            setError(null);
            // A new document invalidates the previous document's expanded wide tier.
            setWideOpen(false);
            wideRequest.current?.abort();
            try {
                const res = await sourceDocumentsApi.impact(code, { signal: controller.signal });
                if (!active) return;
                setData(normalizeImpact(res) as ImpactData);
            } catch (err: any) {
                if (!active || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
                setError(ontologyErrorMessage(err, 'Could not work out what this document affects.'));
                setData(null);
            } finally {
                if (active) setLoading(false);
            }
        })();

        return () => {
            active = false;
            controller.abort();
            wideRequest.current?.abort();
        };
    }, [code]);

    const toggleWide = useCallback(async () => {
        if (wideOpen) { setWideOpen(false); return; }
        setWideOpen(true);

        // Already materialised — opening again must not refetch.
        if (data?.wideIncluded) return;

        const controller = new AbortController();
        wideRequest.current = controller;

        setWideLoading(true);
        try {
            const res = await sourceDocumentsApi.impact(code, {
                includeWide: true,
                signal: controller.signal,
            });
            setData(normalizeImpact(res) as ImpactData);
        } catch (err: any) {
            if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
            setError(ontologyErrorMessage(err, 'Could not load the wider reach.'));
        } finally {
            if (!controller.signal.aborted) setWideLoading(false);
        }
    }, [code, wideOpen, data?.wideIncluded]);

    if (loading) {
        return (
            <Section title="What this document affects" always>
                <CircularProgress size={18} sx={{ color: dsVar('fg-muted') }} />
            </Section>
        );
    }

    if (error || !data) {
        return (
            <Section title="What this document affects" always>
                <Typography sx={{ fontSize: '0.875rem', color: ink('#F87171') }}>
                    {error || 'No impact information available.'}
                </Typography>
            </Section>
        );
    }

    const { tiers, totals } = data;
    // Cited and named are one question to a reader — "which terms does this document
    // reach?" — and the reached_via badge on each row is what separates them.
    const terms = [
        ...tiers.citedBy.filter((r) => r.kind === 'concepts'),
        ...tiers.governs,
    ];
    const citedPolicies = tiers.citedBy.filter((r) => r.kind === 'policies');
    const citedParameters = tiers.citedBy.filter((r) => r.kind === 'parameters');
    const policies = [...citedPolicies, ...tiers.governingPolicies];
    const thresholds = [...citedParameters, ...tiers.thresholds];

    const nothingReached = terms.length === 0 && policies.length === 0
        && thresholds.length === 0 && tiers.agents.length === 0;

    return (
        <>
            <Section title="What this document affects" always>
                {nothingReached ? (
                    <Empty>
                        Nothing in the library cites this document, and it names no term that
                        resolves. A change to it would reach nothing traceable — which for an
                        institution&apos;s own policy usually means the link was never recorded,
                        not that the policy does not matter.
                    </Empty>
                ) : (
                    <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>
                        {totals.concepts} term{totals.concepts === 1 ? '' : 's'}
                        {' · '}{totals.policies} polic{totals.policies === 1 ? 'y' : 'ies'}
                        {' · '}{totals.parameters} threshold{totals.parameters === 1 ? '' : 's'}
                        {' · '}{totals.agents} agent{totals.agents === 1 ? '' : 's'}
                    </Typography>
                )}
            </Section>

            {terms.length > 0 && (
                <Section title="Terms this document defines or is cited by">
                    <RowList rows={terms} />
                </Section>
            )}

            {policies.length > 0 && (
                <Section title="Policies that carry it into force">
                    <RowList rows={policies} />
                </Section>
            )}

            {thresholds.length > 0 && (
                <Section title="Thresholds on those terms">
                    <RowList rows={thresholds} />
                </Section>
            )}

            {tiers.agents.length > 0 && (
                <Section title="Agents affected">
                    {/* The same block a concept's "Used by these agents" renders, so the
                        two read as one finding reached two ways. */}
                    <AgentFamilyList families={tiers.agents} />
                </Section>
            )}

            {/* ── The wide tier ──
                Collapsed, counted, and fetched only on open. */}
            {totals.wide_parameters > 0 && (
                <Section title="Wider reach">
                    <Box
                        onClick={toggleWide}
                        sx={{
                            display: 'flex', alignItems: 'flex-start', gap: 0.5,
                            cursor: 'pointer', userSelect: 'none',
                        }}
                    >
                        {wideOpen
                            ? <ExpandMoreIcon sx={{ fontSize: 18, color: dsVar('fg-muted'), mt: '1px' }} />
                            : <ChevronRightIcon sx={{ fontSize: 18, color: dsVar('fg-muted'), mt: '1px' }} />}
                        <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), lineHeight: 1.55 }}>
                            {totals.wide_parameters} more threshold
                            {totals.wide_parameters === 1 ? '' : 's'} sit under the policies above
                            but are not tied to a term this document names.
                        </Typography>
                    </Box>

                    <Collapse in={wideOpen} unmountOnExit>
                        <Box sx={{ mt: 1.25 }}>
                            {wideLoading
                                ? <CircularProgress size={16} sx={{ color: dsVar('fg-muted') }} />
                                : <RowList rows={tiers.wideParameters} />}
                        </Box>
                    </Collapse>
                </Section>
            )}

            {/* ── The document-to-document chain ── */}
            {data.implements.length > 0 && (
                <Section title="Implements">
                    <RowList rows={data.implements} showVia={false} />
                </Section>
            )}

            {data.implementedBy.length > 0 && (
                <Section title="Implemented by">
                    <RowList rows={data.implementedBy} showVia={false} />
                </Section>
            )}
        </>
    );
};

export default DocumentImpactPanel;
